import React, { useEffect, useState, useContext, useCallback } from "react";
import { Joyride, ACTIONS, EVENTS, STATUS } from "react-joyride";
import { AppContext } from "../../context/app.context";
import { getTourSteps, DEFAULT_LANGUAGE } from "./tourSteps";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #124 react-joyride Time Machine tutorial — tour driver (v3 API).
//
// The tour is LOCKED DOWN: the user only interacts with the tour
// bubble (Next / Back / X). Clicks on the spotlit element and the
// overlay are blocked. The tour driver itself performs every state
// change needed to make the next step's target visible:
//
//   stepIndex transition       side effect
//   ──────────────────────     ────────────────────────────────────
//   0 → 1  (Next)              enterTimeTravel(today)
//   1 → 0  (Back)              exitTimeTravel()
//   2 → 3  (Next)              setIsDiffPairView(false)   — slider visible
//   3 → 2  (Back)              setIsDiffPairView(true)    — back to default
//   4 → 5  (Next)              setIsDiffPairView(true)    — split-screen visible
//   5 → 4  (Back)              setIsDiffPairView(false)   — back to single-grid
//   7 → end (Next/Finish)      exitTimeTravel() + writeCompletion (via onComplete)
//
// Why state-driven rather than user-driven: the user reported their
// own clicks (or stray clicks against the dimmed background) kept
// breaking the tour flow. Lockdown plus driver-managed state changes
// makes the tour deterministic — clicking through Next end-to-end
// always leaves the app in a known state at each step.

const TimeMachineTour = ({
  run,
  onComplete,
  onClose,
  language = DEFAULT_LANGUAGE,
}) => {
  const {
    isTimeTraveling,
    enterTimeTravel,
    exitTimeTravel,
    isDiffPairView,
    setIsDiffPairView,
  } = useContext(AppContext) || {};
  const [stepIndex, setStepIndex] = useState(0);
  const steps = getTourSteps(language);

  // Reset stepIndex whenever the tour is (re)started.
  useEffect(() => {
    if (run) setStepIndex(0);
  }, [run]);

  const handleEvent = useCallback(
    (data) => {
      const { action, index, status, type } = data;

      // - - - Tour-ended detection (broad).
      //
      // v3 controlled-mode is finicky about which event/status combo
      // fires when the user clicks Finish on the last step. Be liberal
      // about what counts as "tour ended" so the orchestrator always
      // gets notified and the overlay unmounts.
      const isLastStepNext =
        type === EVENTS.STEP_AFTER &&
        action === ACTIONS.NEXT &&
        index === steps.length - 1;
      const tourEnded =
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        type === EVENTS.TOUR_END ||
        action === ACTIONS.COMPLETE ||
        action === ACTIONS.SKIP ||
        isLastStepNext;

      if (tourEnded) {
        if (typeof exitTimeTravel === "function" && isTimeTraveling) {
          exitTimeTravel();
        }
        onComplete(status || STATUS.FINISHED);
        return;
      }

      // - - - X-click mid-tour (close button in the tooltip).
      if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER) {
        if (typeof exitTimeTravel === "function" && isTimeTraveling) {
          exitTimeTravel();
        }
        onClose();
        return;
      }

      // - - - Step advance (Next / Back only).
      //
      // Deliberately NOT firing on TARGET_NOT_FOUND. v3 fires that
      // event with `action` set to whatever the LAST action was —
      // including a stale PREV. Reacting to it caused Back-clicks
      // to cascade backwards multiple steps in a single click.
      if (type === EVENTS.STEP_AFTER) {
        if (action !== ACTIONS.NEXT && action !== ACTIONS.PREV) return;
        const isPrev = action === ACTIONS.PREV;

        // State side effects per transition — keep the next step's
        // target reliably in view.
        if (!isPrev) {
          // Forward (Next) transitions:
          if (index === 0 && typeof enterTimeTravel === "function" && !isTimeTraveling) {
            enterTimeTravel(new Date());
          } else if (index === 2 && typeof setIsDiffPairView === "function" && isDiffPairView) {
            // About to show banner slider, which is hidden in Diff Pair.
            setIsDiffPairView(false);
          } else if (index === 4 && typeof setIsDiffPairView === "function" && !isDiffPairView) {
            // About to show the Diff Pair split-screen view.
            setIsDiffPairView(true);
          }
        } else {
          // Backward (Back) transitions — undo the forward side effect:
          if (index === 1 && typeof exitTimeTravel === "function" && isTimeTraveling) {
            exitTimeTravel();
          } else if (index === 3 && typeof setIsDiffPairView === "function" && !isDiffPairView) {
            setIsDiffPairView(true);
          } else if (index === 5 && typeof setIsDiffPairView === "function" && isDiffPairView) {
            setIsDiffPairView(false);
          }
        }

        const proposed = index + (isPrev ? -1 : 1);
        const nextIndex = Math.max(0, Math.min(proposed, steps.length - 1));
        setStepIndex(nextIndex);
      }
    },
    [
      onComplete,
      onClose,
      isTimeTraveling,
      isDiffPairView,
      enterTimeTravel,
      exitTimeTravel,
      setIsDiffPairView,
      steps.length,
    ]
  );

  if (!run) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      onEvent={handleEvent}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip Tour",
      }}
      // v3 defaults — top-level `options` is the global step config.
      // Per-step values in tourSteps.js can override any of these.
      options={{
        primaryColor: "#704214", // sepia brown
        overlayColor: "rgba(0, 0, 0, 0.55)",
        zIndex: 10000,
        // LOCKDOWN — block clicks on the spotlit element (the dim
        // overlay's spotlight cutout) so the user can't accidentally
        // (or deliberately) click app UI while the tour is running.
        blockTargetInteraction: true,
        // Clicking the overlay does nothing. Default 'close' was
        // a footgun — one stray click outside the bubble closed the
        // whole tour.
        overlayClickAction: false,
      }}
      // Element-specific CSS overrides.
      styles={{
        tooltip: {
          borderRadius: "0.5rem",
          backgroundColor: "#f5deb3",
          color: "#1a1a1a",
        },
        tooltipTitle: { color: "#1a1a1a" },
        tooltipContent: { color: "#1a1a1a" },
        buttonPrimary: {
          backgroundColor: "#704214",
          color: "#f5deb3",
          borderRadius: "0.25rem",
        },
        buttonBack: { color: "#704214" },
        buttonSkip: { color: "#704214" },
        arrow: { color: "#f5deb3" },
      }}
    />
  );
};

export default TimeMachineTour;
